<template>
    <div>
        <Navigation />
        <!-- If the user is not a pro premium, show the not premium card -->
        <v-container v-if="$acl.not.check('isProPremium')">
            <v-row>
                <v-col cols="12">
                    <NotPremiumCard/>
                </v-col>
            </v-row>
        </v-container>
        <!-- If the user is a pro premium, show the licence page -->
        <v-container v-else>
            <!-- Header -->
            <v-row>
                <v-col cols="12">
                    <v-card elevation="4" color="primary" class="mb-3">
                        <v-card-text class="px-4 py-3">
                            <v-row align="center" no-gutters>
                                <v-col cols="auto">
                                    <v-icon x-large class="mr-4 white--text">school</v-icon>
                                </v-col>
                                <v-col>
                                    <h1 class="headline mb-1 white--text">Espace Licence - Supports de séances</h1>
                                    <p class="subtitle-1 mb-0 white--text">
                                        Accédez à tous les supports de séances mis à disposition pour les professionnels bénéficiant du statut Licence.
                                    </p>
                                </v-col>
                            </v-row>
                            <v-row align="center" class="mt-4">
                                <v-col cols="12" md="4" class="pr-md-4">
                                    <v-autocomplete
                                        v-model="selectedSequence"
                                        :items="sequenceOptions"
                                        label="Filtrer par n° de séquence"
                                        clearable
                                        flat
                                        solo
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" md="8"></v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Table of supports -->
            <v-card flat>
                <v-data-table
                    :headers="headers"
                    :items="filteredSupports"
                    :loading="loading"
                    :items-per-page="-1"
                    hide-default-footer
                    no-data-text="Aucun support disponible"
                    class="elevation-1"
                >
                    <template v-slot:item.sequence="{ item }">
                        <v-chip color="primary" text-color="white" class="font-weight-bold">
                            Séquence {{ item.sequence }} {{ item.support_type == 'aja' ? '(AJA)' : '' }}
                        </v-chip>
                    </template>

                    <template v-slot:item.pro_attachment="{ item }">
                        <div v-if="item.pro_attachment" class="d-flex flex-column align-center">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        v-on="on"
                                        color="primary"
                                        @click.stop="openIspringInNewTab('pro_attachment', item.pro_attachment)"
                                        small
                                        depressed
                                        text
                                        class="mb-1"
                                    >
                                        <v-icon left small>open_in_new</v-icon>
                                        Ouvrir le diapo
                                    </v-btn>
                                </template>
                                <span>Voir les diapositives</span>
                            </v-tooltip>
                        </div>
                        <v-chip v-else color="grey" text-color="white" small>
                            Non disponible
                        </v-chip>
                    </template>

                    <template v-slot:item.user_attachment="{ item }">
                        <div v-if="item.user_attachment" class="d-flex justify-center align-center">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        v-on="on"
                                        color="primary"
                                        @click.stop="openPdfInNewTab(item.user_attachment)"
                                        small
                                        depressed
                                        text
                                    >
                                        <v-icon left small>open_in_new</v-icon>
                                        Ouvrir le livret
                                    </v-btn>
                                </template>
                                <span>Ouvrir le livret</span>
                            </v-tooltip>
                        </div>
                        <v-chip v-else color="grey" text-color="white" small>
                            Non disponible
                        </v-chip>
                    </template>

                    <template v-slot:item.guide_attachment="{ item }">
                        <div v-if="item.guide_attachment" class="d-flex flex-column align-center">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        v-on="on"
                                        color="primary"
                                        @click.stop="openIspringInNewTab('guide_attachment', item.guide_attachment)"
                                        small
                                        depressed
                                        text
                                        class="mb-1"
                                    >
                                        <v-icon left small>open_in_new</v-icon>
                                        Ouvrir le guide
                                    </v-btn>
                                </template>
                                <span>Voir le guide professionnel</span>
                            </v-tooltip>
                        </div>
                        <v-chip v-else color="grey" text-color="white" small>
                            Non disponible
                        </v-chip>
                    </template>
                </v-data-table>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import JSZip from 'jszip'
import NotPremiumCard from '@/components/NotPremiumCard.vue'
import Navigation from '@/components/Navigation.vue'
import { generateAccessControlScript } from '@/utils/iSpringAccessControl'

export default {
    name: 'Licence',
    components: { NotPremiumCard, Navigation },
    data () {
        return {
            supports: [],
            search: {
                shared_with_licence_pros: true
            },
            loading: false,

            headers: [
                { text: 'N° Séquence', align: 'center', value: 'sequence', sortable: true },
                { text: 'Diapositives', value: 'pro_attachment', align: 'center', sortable: false },
                { text: 'Livret', value: 'user_attachment', align: 'center', sortable: false },
                { text: 'Guide professionnel', value: 'guide_attachment', align: 'center', sortable: false },
            ],
            selectedSequence: null,
        }
    },
    computed: {
        sequenceOptions() {
            // Get the sequences from the supports
            // Filter out null values
            const seqs = this.supports.map(s => s.sequence).filter(Boolean)
            // Get the unique sequences with the text "Séquence" and the number
            return [...new Set(seqs)].sort((a, b) => {
                if (!isNaN(a) && !isNaN(b)) return Number(a) - Number(b)
                return (''+a).localeCompare(b)
            }).map(seq => ({
                text: `Séquence ${seq}`,
                value: seq
            }))
        },
        filteredSupports() {
            // If no sequence is selected, return all supports
            if (!this.selectedSequence) {
                return this.supports
            }
            // If a sequence is selected, return the supports that match the selected sequence
            return this.supports.filter(support => support.sequence === this.selectedSequence)
        },
    },
    mounted () {
        this.fetchSupports()
    },
    methods: {
        fetchSupports () {
            this.loading = true
            this.supports = []
            this.$store.dispatch('supportsRequest', {
                per_page: 1000,
                search: JSON.stringify(this.search),
                with: ['pro_attachment', 'user_attachment', 'guide_attachment']
            })
                .then(result => {
                    this.supports = result.data
                    this.loading = false
                })
                .catch((err) => {
                    this.loading = false
                    if (err.response.status != 403)
                        this.$store.commit('setSnack', {'type': 'red', 'msg': err.response.data})
                })
        },
        downloadAttachment(attachment) {
            return this.$store.dispatch('attachmentDownloadRequest', { id: attachment.id}).then((data) => {
                const url = window.URL.createObjectURL(new Blob([data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', attachment.name);
                document.body.appendChild(link);
                link.click();
            });
        },
        async openPdfInNewTab(attachment) {
            try {
                const response = await this.$store.dispatch('attachmentDownloadRequest', { id: attachment.id });
                const blob = new Blob([response], { type: 'application/pdf' });
                const pdfUrl = URL.createObjectURL(blob);
                window.open(pdfUrl, '_blank');
            } catch (e) {
                console.error('Erreur ouverture PDF', e);
                this.$store.commit('setSnack', {'type': 'error', 'msg': 'Erreur lors de l\'ouverture du PDF'});
            }
        },

        async openIspringInNewTab(type, attachment) {
            try {
                const zipData = await this.$store.dispatch('attachmentDownloadRequest', { id: attachment.id });
                // Load the zip file from a blob
                const zipRoot = await JSZip.loadAsync(zipData);
                // Get the HTML file from the zip
                const htmlFile = zipRoot.file("index.html");
                // Get the contents of the HTML file
                let htmlContent = await htmlFile.async("string");
                // Create a base URL that points to the blob object
                const baseUrl = process.env.VUE_APP_API_URL + `/api/supports/${attachment.attachable_id}/${type}/proxy/`;
                // Inject base tag in the head with baseUrl as href attribute
                var parser = new DOMParser();
                var doc = parser.parseFromString(htmlContent, 'text/html');
                var head = doc.getElementsByTagName('head')[0];
                var baseElement = document.createElement('base');
                baseElement.setAttribute('href', baseUrl)
                head.appendChild(baseElement);

                // Injecter le script de contrôle d'accès (timer 2h10 uniquement, pas de meeting)
                const accessControlScript = generateAccessControlScript({
                    apiUrl: process.env.VUE_APP_API_URL,
                    supportId: attachment.attachable_id,
                    type: type,
                    meetingId: null,
                    meetingEnd: null
                });
                const scriptElement = doc.createElement('script');
                scriptElement.textContent = accessControlScript;
                doc.body.appendChild(scriptElement);

                // Open the preview content in a new browser tab
                const previewContent = doc.documentElement.outerHTML;
                const previewWindow = window.open();
                previewWindow.document.write(previewContent);
                previewWindow.document.close();
            } catch (e) {
                console.error('Erreur ouverture diaporama', e);
                this.$store.commit('setSnack', {'type': 'error', 'msg': 'Erreur lors de l\'ouverture du diaporama'});
            }
        },
    },
}
</script>